import type { FocusEvent, ReactNode, Ref } from 'react';
import { Input, Label, Text, TextArea, TextField as AriaTextField } from 'react-aria-components';
import styles from './TextField.module.css';

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (event: FocusEvent<Element>) => void;
  description?: ReactNode;
  /** Shown under the field and linked with aria-describedby. */
  error?: string | undefined;
  /** Positive validation detail, shown and linked with aria-describedby. */
  success?: string | undefined;
  /** Extra content under the description (e.g. a normalization note). */
  after?: ReactNode;
  name?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'url' | 'numeric';
  /** Hides the value while typing (tokens, secrets). */
  type?: 'text' | 'password' | 'email' | 'datetime-local' | 'date';
  mono?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  inputRef?: Ref<HTMLInputElement>;
  id?: string;
  /** Keeps the label for assistive tech only (toolbar search with a placeholder). */
  hideLabel?: boolean;
  /** A few lines of prose (a reason or note) instead of a single-line value. */
  multiline?: boolean;
}

export function TextField({
  label,
  value,
  onChange,
  onBlur,
  description,
  error,
  success,
  after,
  name,
  autoComplete,
  inputMode,
  type = 'text',
  mono = false,
  isReadOnly = false,
  isDisabled = false,
  placeholder,
  inputRef,
  id,
  hideLabel = false,
  multiline = false,
}: TextFieldProps) {
  return (
    <AriaTextField
      className={styles.field}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      isInvalid={Boolean(error)}
      data-has-success={success ? '' : undefined}
      isReadOnly={isReadOnly}
      isDisabled={isDisabled}
      validationBehavior="aria"
      type={type}
      {...(name ? { name } : {})}
      {...(autoComplete ? { autoComplete } : {})}
      {...(id ? { id } : {})}
    >
      <Label className={hideLabel ? 'sr-only' : styles.label}>{label}</Label>
      {multiline ? (
        <TextArea
          className={[styles.input, styles.multiline].join(' ')}
          rows={3}
          placeholder={placeholder}
        />
      ) : (
        <Input
          ref={inputRef}
          className={[styles.input, mono ? styles.mono : ''].join(' ')}
          spellCheck={false}
          autoCapitalize="off"
          placeholder={placeholder}
          {...(inputMode ? { inputMode } : {})}
        />
      )}
      {description && (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      )}
      {error && (
        <Text slot="errorMessage" className={styles.error}>
          {error}
        </Text>
      )}
      {success && (
        <Text slot="description" className={styles.success}>
          {success}
        </Text>
      )}
      {after}
    </AriaTextField>
  );
}
